import { ArrowDown, ArrowUp, ArrowUpDown, ClipboardList } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Pager } from '@/components/common/Pager'
import { EmptyState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Select } from '@/components/ui/select'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { PROJECT_WORK_PERM } from '../../permissions'
import { useAssignTask, useMoveTask } from '../../taskApi'
import { applyTaskFilters, isOverdue, type TaskFilters } from '../../taskFilters'
import { PRIORITIES, TASK_STATUSES, type Project, type Task } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { RowMenu } from '../RowMenu'
import { PriorityBadge, TaskStatusBadge, TaskTypeBadge } from '../StatusBadges'
import { TaskFormModal } from '../task/TaskFormModal'
import { useTaskDelete } from '../task/useTaskDelete'

type SortKey = 'key' | 'title' | 'priority' | 'status' | 'dueDate'
const rank = { priority: (t: Task) => PRIORITIES.indexOf(t.priority), status: (t: Task) => TASK_STATUSES.indexOf(t.status) }

export function ListTab({ project, filters }: { project: Project; filters: TaskFilters }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { nameOf } = useStaffLookup()
  const move = useMoveTask(project.id)
  const assign = useAssignTask(project.id)
  const onDelete = useTaskDelete(project)
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'dueDate', dir: 1 })
  const [editing, setEditing] = useState<Task | null>(null)
  const canWrite = can(PROJECT_WORK_PERM)

  const rows = useMemo(() => {
    const list = applyTaskFilters(project.tasks, filters)
    const val = (x: Task): string | number => (sort.key === 'priority' || sort.key === 'status' ? rank[sort.key](x) : x[sort.key])
    return [...list].sort((a, b) => {
      const av = val(a)
      const bv = val(b)
      return (typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'tr', { numeric: true })) * sort.dir
    })
  }, [project.tasks, filters, sort])
  const { pageRows, pager } = usePagination(rows, [filters, sort])

  const customerOf = new Map(project.installations.map((i) => [i.id, i.customer]))
  const people = [{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId) }))]

  const header = (key: SortKey, label: string, className?: string) => {
    const Icon = sort.key !== key ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown
    return (
      <TH className={className} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
        <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))} className="inline-flex items-center gap-1 uppercase hover:text-foreground">
          {label}
          <Icon className="size-3" aria-hidden />
        </button>
      </TH>
    )
  }
  const link = (task: Task) => (
    <Link to={`/projects/${project.id}/tasks/${task.id}`} className="font-medium hover:text-primary hover:underline">
      {task.title}
    </Link>
  )
  const menu = (task: Task) => (canWrite ? <RowMenu label={task.key} onEdit={() => setEditing(task)} onDelete={() => void onDelete(task)} /> : null)

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-card shadow-card">
        <EmptyState icon={ClipboardList} title={project.tasks.length ? t('projects.tasks.noMatch') : t('projects.tasks.empty')} />
      </div>
    )
  }

  return (
    <div className="@container overflow-hidden rounded-xl border bg-card shadow-card">
      <ul className="divide-y @3xl:hidden">
        {pageRows.map((task) => (
          <li key={task.id} className="space-y-2 p-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <span className="font-mono text-[11px] text-muted-foreground">{task.key}</span>
                <div className="text-sm">{link(task)}</div>
              </div>
              {menu(task)}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <TaskStatusBadge status={task.status} />
              <PriorityBadge priority={task.priority} compact />
              <TaskTypeBadge type={task.type} />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{task.assigneeId ? nameOf(task.assigneeId) : t('projects.work.unassigned')}</span>
              <span className={cn('tabular-nums', isOverdue(task) && 'font-medium text-destructive')}>{fmtDate(task.dueDate)}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden @3xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              {header('key', t('projects.tasks.fields.key'), 'w-24')}
              {header('title', t('projects.tasks.fields.title'))}
              <TH className="hidden @5xl:table-cell">{t('projects.tasks.fields.customer')}</TH>
              <TH className="w-44">{t('projects.tasks.fields.assignee')}</TH>
              {header('priority', t('projects.tasks.fields.priority'), 'hidden @4xl:table-cell')}
              {header('status', t('projects.tasks.fields.status'), 'w-40')}
              {header('dueDate', t('projects.tasks.fields.dueDate'), 'text-right')}
              {canWrite ? <TH className="w-12" aria-label={t('projects.work.columns.actions')} /> : null}
            </TR>
          </THead>
          <TBody>
            {pageRows.map((task) => (
              <TR key={task.id}>
                <TD className="font-mono text-xs text-primary">{task.key}</TD>
                <TD className="max-w-80">
                  <div className="truncate">{link(task)}</div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <TaskTypeBadge type={task.type} />
                    {task.parentId ? <span className="text-[11px] text-muted-foreground">{t('projects.tasks.subtask')}</span> : null}
                  </div>
                </TD>
                <TD className="hidden text-sm text-muted-foreground @5xl:table-cell">{task.installationId ? customerOf.get(task.installationId) : '—'}</TD>
                <TD>
                  {canWrite ? (
                    <Select size="xs" value={task.assigneeId ?? ''} options={people} aria-label={t('projects.tasks.fields.assignee')} onChange={(v) => assign.mutate({ taskId: task.id, assigneeId: v || null }, { onError: toastError })} />
                  ) : (
                    <span className="text-sm">{task.assigneeId ? nameOf(task.assigneeId) : '—'}</span>
                  )}
                </TD>
                <TD className="hidden @4xl:table-cell">
                  <PriorityBadge priority={task.priority} compact />
                </TD>
                <TD>
                  {canWrite ? (
                    <Select
                      size="xs"
                      value={task.status}
                      aria-label={t('projects.tasks.fields.status')}
                      options={TASK_STATUSES.map((s) => ({ value: s, label: t(`projects.taskStatus.${s}`) }))}
                      onChange={(status) => move.mutate({ taskId: task.id, status }, { onError: toastError })}
                    />
                  ) : (
                    <TaskStatusBadge status={task.status} />
                  )}
                </TD>
                <TD className={cn('whitespace-nowrap text-right text-sm tabular-nums', isOverdue(task) && 'font-medium text-destructive')}>{fmtDate(task.dueDate)}</TD>
                {canWrite ? <TD className="text-right">{menu(task)}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <Pager {...pager} />
      <p className="border-t px-3 py-2 text-xs text-muted-foreground">{t('projects.tasks.count', { count: rows.length, total: project.tasks.length })}</p>
      <TaskFormModal open={!!editing} project={project} task={editing} onOpenChange={(v) => !v && setEditing(null)} />
    </div>
  )
}
